// The four PURE halves of `src/features/agent-harness/stream/stream-http.ts`.
//
// A MIXED CARVE, like `client.ts` before it. The fifth export, `streamHeaders`,
// stays app-side: it mints an `Authorization` header from the auth token and the
// `X-Client-*` identity headers, which is platform state a headless package may
// not hold. It was already the last thing keeping this family's `query`
// dimension amber, and it crosses the same way `transport` did — as a required
// parameter on the two stream hooks, with the app-side delegate re-applying the
// default.
//
// The four that moved carry no app reach at all: an error class, a status
// predicate, a response-body reader and an abortable sleep. `retry-policy.ts`
// reads two of them, which is why it travelled in the same slice.
export class StreamHttpError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'StreamHttpError';
  }
}

export const shouldRetryStatus = (status: number): boolean =>
  status >= 500 || status === 408 || status === 429;

export const responseError = async (response: Response): Promise<string> => {
  const body = await response.text().catch(() => '');
  if (!body.trim()) return response.statusText || String(response.status);

  try {
    const parsed = JSON.parse(body) as { error?: unknown; detail?: unknown };
    if (typeof parsed.error === 'string') {
      if (parsed.detail === undefined || parsed.detail === null) return parsed.error;
      const detail =
        typeof parsed.detail === 'string' ? parsed.detail : JSON.stringify(parsed.detail);
      return `${parsed.error} (${detail})`;
    }
  } catch {
    return `${response.status}: ${body}`;
  }

  return `${response.status}: ${body}`;
};

export const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(timer);
      resolve();
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
